import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo, useState, type FormEvent } from 'react';
import { ApiError, api, type Me } from '../api';
import { errorText, useI18n } from '../i18n';
import { Alert, PageHead, PanelShell } from '../layout';

interface CalendarStatus { clientId: string; hasSecret: boolean; connected: boolean; accountEmail: string | null; redirectUri: string }
interface CalendarEvent { id: string; summary: string; description: string; allDay: boolean; start: string; end: string; taskId: number | null }

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const NOTICES: Record<string, [kind: 'success' | 'error', text: string]> = {
  connected: ['success', 'Google kalendorius prijungtas.'],
  cancelled: ['error', 'Prijungimas atšauktas.'],
  oauth_state_invalid: ['error', 'Prijungimo nuoroda nebegalioja. Bandykite dar kartą.'],
  oauth_no_refresh_token: ['error', 'Google nesuteikė nuolatinės prieigos. Pašalinkite programos prieigą Google paskyroje ir bandykite dar kartą.'],
  oauth_scope_missing: ['error', 'Nesuteiktas leidimas kalendoriui. Bandykite dar kartą ir pažymėkite kalendoriaus leidimą.'],
};

export function CalendarPage({ me, notice }: { me: Me; notice?: string }) {
  const { t, locale } = useI18n();
  const queryClient = useQueryClient();
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const status = useQuery({ queryKey: ['calendar'], queryFn: () => api.get<CalendarStatus>('api/v1/calendar') });
  const from = iso(new Date(month.getFullYear(), month.getMonth(), 1));
  const to = iso(new Date(month.getFullYear(), month.getMonth() + 1, 0));
  const events = useQuery({ queryKey: ['calendar-events', from], enabled: Boolean(status.data?.connected), queryFn: () => api.get<{ events: CalendarEvent[] }>(`api/v1/calendar/events?from=${from}&to=${to}`).then((r) => r.events) });
  const refresh = () => Promise.all([queryClient.invalidateQueries({ queryKey: ['calendar'] }), queryClient.invalidateQueries({ queryKey: ['calendar-events'] })]);
  const credentials = useMutation({ mutationFn: (body: unknown) => api.put('api/v1/calendar/credentials', body), onSuccess: refresh });
  const connect = useMutation({ mutationFn: () => api.post<{ url: string }>('api/v1/calendar/connect'), onSuccess: (r) => { window.location.assign(r.url); } });
  const disconnect = useMutation({ mutationFn: () => api.delete('api/v1/calendar/connection'), onSuccess: refresh });
  const addEvent = useMutation({ mutationFn: (body: unknown) => api.post('api/v1/calendar/events', body), onSuccess: refresh });
  const removeEvent = useMutation({ mutationFn: (id: string) => api.delete(`api/v1/calendar/events/${id}`), onSuccess: refresh });

  const days = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const start = new Date(first);
    start.setDate(1 - ((first.getDay() + 6) % 7)); // weeks start on Monday
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [month]);
  const byDay = (d: Date) => (events.data ?? []).filter((e) => e.start.slice(0, 10) === iso(d));
  const s = status.data;
  const flash = notice ? NOTICES[notice] ?? ['error', 'Prijungti nepavyko. Patikrinkite Client ID ir Secret.'] : null;
  const error = credentials.error ?? connect.error ?? disconnect.error ?? addEvent.error ?? removeEvent.error ?? events.error;

  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Darbas')} title={t('Kalendorius')} text={t('Užduotys su terminu ir jūsų Google kalendoriaus įvykiai.')} />}>
      {flash ? <Alert kind={flash[0]}>{t(flash[1])}</Alert> : null}
      {error ? <Alert kind="error">{error instanceof ApiError && error.code === 'calendar_access_lost' ? t('Google prieiga nebegalioja. Prijunkite kalendorių iš naujo.') : errorText(t, error)}</Alert> : null}
      <div className="two-cols two-cols--form">
        <section className="panel-card">
          <div className="card-head">
            <div><p className="eyebrow">{month.getFullYear()}</p><h2>{month.toLocaleDateString(locale, { month: 'long' })}</h2></div>
            <div className="form-actions">
              <button type="button" className="button button--ghost button--small" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}>‹</button>
              <button type="button" className="button button--ghost button--small" onClick={() => { const d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)); }}>{t('Šiandien')}</button>
              <button type="button" className="button button--ghost button--small" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}>›</button>
            </div>
          </div>
          {!s?.connected ? <p className="muted">{t('Prijunkite Google kalendorių dešinėje. Užduotys su terminu bus rodomos kalendoriuje.')}</p> : null}
          <div className="calendar-grid">
            {days.slice(0, 7).map((d) => <div key={iso(d)} className="calendar-weekday">{d.toLocaleDateString(locale, { weekday: 'short' })}</div>)}
            {days.map((d) => (
              <div key={iso(d)} className={`calendar-day ${d.getMonth() !== month.getMonth() ? 'is-other' : ''} ${iso(d) === iso(new Date()) ? 'is-today' : ''}`}>
                <span className="calendar-date">{d.getDate()}</span>
                {byDay(d).map((e) => (
                  <div key={e.id} className={`calendar-event ${e.taskId ? 'is-task' : ''}`} title={e.description}>
                    {e.allDay ? '' : `${e.start.slice(11, 16)} `}{e.summary}
                    {e.taskId ? <Link to="/tasks" className="calendar-event-link"> →</Link> : <button type="button" className="delete-x" aria-label={t('Pašalinti')} onClick={() => { if (confirm(t('Pašalinti įvykį?'))) removeEvent.mutate(e.id); }}>×</button>}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </section>
        <aside className="panel-card">
          {s?.connected ? (
            <details className="fold">
              <summary><div><p className="eyebrow">{t('Prijungta')}</p><h2>{s.accountEmail}</h2></div></summary>
              <form onSubmit={(e: FormEvent<HTMLFormElement>) => {
                e.preventDefault();
                const d = new FormData(e.currentTarget);
                addEvent.mutate({ summary: d.get('summary'), date: d.get('date'), ...(d.get('start') ? { start: d.get('start'), end: d.get('end') || d.get('start') } : {}), description: d.get('description') || undefined });
                e.currentTarget.reset();
              }}>
                <p className="form-section">{t('Naujas įvykis')}</p>
                <label>{t('Pavadinimas')}<input name="summary" required /></label>
                <div className="form-grid">
                  <label>{t('Data')}<input type="date" name="date" defaultValue={iso(new Date())} required /></label>
                  <label>{t('Pradžia')}<input type="time" name="start" /></label>
                  <label>{t('Pabaiga')}<input type="time" name="end" /></label>
                </div>
                <label>{t('Aprašymas')}<textarea name="description" rows={2} /></label>
                <button className="button button--primary" disabled={addEvent.isPending}>{t('Pridėti įvykį')}</button>
              </form>
              <hr />
              <button type="button" className="button button--ghost" onClick={() => { if (confirm(t('Atjungti Google kalendorių?'))) disconnect.mutate(); }}>{t('Atjungti kalendorių')}</button>
            </details>
          ) : (
            <>
              <p className="eyebrow">{t('Nustatymai')}</p><h2>{t('Google kalendorius')}</h2>
              <p className="muted">{t('Google Cloud Console sukurkite OAuth klientą (Web application) ir įrašykite šį leistiną nukreipimo adresą:')}</p>
              <code className="calendar-callback">{s?.redirectUri}</code>
              <form onSubmit={(e: FormEvent<HTMLFormElement>) => {
                e.preventDefault();
                const d = new FormData(e.currentTarget);
                credentials.mutate({ clientId: d.get('clientId'), ...(d.get('clientSecret') ? { clientSecret: d.get('clientSecret') } : {}) });
              }}>
                <label>Client ID<input name="clientId" defaultValue={s?.clientId ?? ''} required autoComplete="off" /></label>
                <label>Client Secret<input type="password" name="clientSecret" autoComplete="new-password" placeholder={s?.hasSecret ? t('Išsaugotas — palikite tuščią, kad nepasikeistų') : ''} /></label>
                <button className="button button--ghost" disabled={credentials.isPending}>{t('Išsaugoti')}</button>
              </form>
              <hr />
              <button type="button" className="button button--primary" disabled={!s?.clientId || !s.hasSecret || connect.isPending} onClick={() => connect.mutate()}>{t('Prijungti Google kalendorių')}</button>
            </>
          )}
        </aside>
      </div>
    </PanelShell>
  );
}
